import {
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { Close } from "@mui/icons-material";
import { AppTheme } from "../../../theme";
import { LoadingButton } from "../../../components/shared/LoadingButton";
import { PlanTableFormat } from "../../../types/planning/plan.type";
import { finalStatus, planningStatus } from "../../../types/status.type";
import StatusTag from "../../../components/shared/StatusTag/StatusTag";
import { toast } from "../../../utils/snackbarHelper";
import { useDeleteRouteAssignment } from "../../../hooks/react-query/useRouteAssignment";
import { useDeleteDeliveryTrapsAssignment } from "../../../hooks/react-query/useDeliveryTrapAssignment";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";
import { useDateSearchParamOrFallbackToToday } from "../../../hooks/useDateSearchParamOrFallbackToToday";
import { dateToInt } from "../../../utils/dateTimeHelper";
import { actionsMap, type ShipmentActionTypeId } from "./ShipmentActionDialog";

interface DeleteRouteAndTrapAssignmentConfirmDialogProps {
  plans: PlanTableFormat[];
  open: boolean;
  clearSelection?: () => void;
  onClose?: () => void;
}
interface RouteTrapWithStatus {
  plan: PlanTableFormat;
}
export const RouteTrapWithStatus = ({ plan }: RouteTrapWithStatus) => (
  <Box display="flex" alignItems="center" gap={1} mb={1}>
    <Typography variant="body2">{plan.name}</Typography>
    <StatusTag label={plan.status} type="pill" isPlannedFor="today" />
  </Box>
);

export default function DeleteRouteAndTrapAssignmentConfirmDialog({
  plans,
  open,
  onClose = () => {},
  clearSelection = () => {},
}: DeleteRouteAndTrapAssignmentConfirmDialogProps) {
  const [serviceCenter] = useSelectedServiceCenter();
  const [selectedDate] = useDateSearchParamOrFallbackToToday();
  const dateInt = dateToInt(selectedDate);

  const routeIds = plans
    .filter((x) => x.type === "delivery_route")
    .map((x) => x.id);

  const onSuccess = (actionType: ShipmentActionTypeId) => {
    const { description } = actionsMap[actionType];
    toast(description, {
      variant: "success",
    });
  };

  const { mutate: deleteRoutes, isPending: isRouteDeletePending } =
    useDeleteRouteAssignment({
      date: dateInt,
      sicId: serviceCenter.id,
      searchTerm: "",
      onSuccess: () =>
        onSuccess(routeIds.length > 1 ? "deleteRoutes" : "deleteRoute"),
      onSettled: () => {
        clearSelection();
        onClose();
      },
    });

  const { mutate: deleteTraps, isPending: isTrapDeletePending } =
    useDeleteDeliveryTrapsAssignment({
      date: dateInt,
      sicId: serviceCenter.id,
      searchTerm: "",
      onSuccess: () => onSuccess("deleteDeliveryTraps"),
      onSettled: () => {
        clearSelection();
        onClose();
      },
    });

  const isLoading = isRouteDeletePending || isTrapDeletePending;

  const handleDeleteConfirm = () => {
    if (routeIds.length > 0) deleteRoutes({ planIds: routeIds });

    const trapIds = plans
      .filter((x) => x.type === "delivery_trap")
      .map((x) => x.id);

    if (trapIds.length > 0) deleteTraps({ trapIds });
  };

  if (plans.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "600", padding: "20px 0px 10px 20px" }}>
          Deletion Requirements
        </DialogTitle>
        <Box position="absolute" top={4} right={4}>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ padding: "10px 20px" }}>
          Please select at least one row to delete.
        </DialogContent>
        <DialogActions sx={{ margin: "10px 20px 20px 20px" }}>
          <Button
            onClick={onClose}
            sx={{ backgroundColor: AppTheme.palette.primary.main }}
            variant="contained"
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const errorGroups = [
    {
      message:
        "A route or trap must have zero bills before deleting. Please remove any associated bills.",
      plans: plans.filter((p) => p.bills > 0),
    },
    {
      message: "A route cannot be deleted when in CLDV.",
      plans: plans.filter((p) => p.status === planningStatus.CLDV),
    },
    {
      message: "A trap cannot be deleted when in TRAP.",
      plans: plans.filter(
        (p) => p.type === "delivery_trap" && p.status === "TRAP"
      ),
    },
    {
      message: "A route cannot be deleted when in DISP.",
      plans: plans.filter((p) => p.status === planningStatus.DISP),
    },
    {
      message: "A route cannot be deleted when in DEPT.",
      plans: plans.filter((p) => p.status === finalStatus.DEPT),
    },
  ].filter((group) => group.plans.length > 0);

  const hasError = errorGroups.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: "600", padding: "20px 0px 10px 20px" }}>
        {hasError
          ? "Deletion Requirements"
          : `Delete "${plans.map((p) => p.name).join(", ")}"?`}
      </DialogTitle>
      <Box position="absolute" top={4} right={4}>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      {hasError ? (
        <>
          <DialogContent sx={{ padding: "10px 20px", whiteSpace: "pre-wrap" }}>
            {errorGroups.map((group) => (
              <Box key={group.message} mb={2}>
                <Box fontWeight="bold" mb={1}>
                  {group.message}
                </Box>
                {group.plans.map((plan) => (
                  <RouteTrapWithStatus key={plan.id} plan={plan} />
                ))}
              </Box>
            ))}
          </DialogContent>
          <DialogActions sx={{ margin: "10px 20px 20px 20px" }}>
            <Button
              onClick={onClose}
              sx={{ backgroundColor: AppTheme.palette.primary.main }}
              variant="contained"
            >
              Ok
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent
            sx={{ padding: "10px 0px 0px 20px", minHeight: "31px" }}
          >
            {`Are you sure you want to delete: "${plans
              .map((p) => p.name)
              .join(", ")}"? This action cannot be undone.`}
          </DialogContent>
          <DialogActions sx={{ margin: "10px 20px 20px 20px" }}>
            <Button
              onClick={onClose}
              sx={{ color: "black" }}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleDeleteConfirm}
              sx={{ backgroundColor: AppTheme.palette.error.main }}
              color="error"
              variant="contained"
              loadingLabel="Deleting..."
              isLoading={isLoading}
            >
              Delete
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
